'use client';

import TopSelector from '@fe-common-ui/Group/TopSelector/TopSelector';
import { styled } from '@linaria/react';
import { useCallback, useState, useEffect } from 'react';
import { AiArticleType, AI_TABS, DEFAULT_AI_CONTENT } from '@fe-news/constants/ai/type';
import Title from '@fe-news/components/atoms/AsideAiNews/Title';
import Content from '@fe-news/components/atoms/AsideAiNews/Content';
import { fetchAiNews } from '@fe-news/actions/ai';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import Error from '@fe-news/components/atoms/AsideAiNews/Error';
import Loading from '@fe-news/components/atoms/AsideAiNews/Loading';

const DEFAULT_TAB = 'headline';

const ContentWrapper = styled.div`
  position: relative;
  background-color: white;
  padding: 0px 24px 16px 24px;
  margin-top: 12px;
  border-radius: 6px;
  border: solid 1px #e2e8f1;
  height: 247px;
  overflow: hidden;

  background-image: url('/assets/images/ai-bg.png');
  background-size: 375px 259px;
  background-repeat: no-repeat;
  background-position: bottom right;
`;

const Container = styled.div`
  height: 247px;
  position: relative;
  z-index: 1;
`;

const ReadMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
`;

const BackgroundImage = styled.div`
  position: absolute;
  right: 0;
  bottom: -8px;
  width: 375px;
  height: 259px;
  z-index: 0;
`;

const AsideAiNewsContent = () => {
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [isTitleLoaded, setIsTitleLoaded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['aside-ai-news', tab],
    queryFn: () => fetchAiNews({ type: tab as AiArticleType })
  });

  const onChangeTab = useCallback((category: string | number) => {
    const categoryName = category === 'us_stock' ? '美股' : category === 'tw_stock' ? '台股' : '頭條';

    setTab(category as AiArticleType);
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TAB,
      [GTMEventKey.SECTION]: `News_Category_${categoryName}＿AI讀新聞`,
      [GTMEventKey.CLICK_ITEM]: categoryName
    });
  }, []);

  const { data: items = DEFAULT_AI_CONTENT, isError } = data ?? {};

  const { title = DEFAULT_AI_CONTENT.title, summary = DEFAULT_AI_CONTENT.summary } = items;

  useEffect(() => {
    setIsTitleLoaded(false);
  }, [tab]);

  const url = tab === 'us_stock' ? `/news/cat/wd_stock_ai` : `/news/cat/${tab}_ai`;

  return (
    <ContentWrapper>
      <Container>
        <TopSelector selectors={AI_TABS} selector={tab} onClick={onChangeTab} />
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            {isError && <Error />}
            {!isError && (
              <>
                <Title setIsTitleLoaded={setIsTitleLoaded} title={title} />
                {isTitleLoaded && <Content content={summary} />}
                <ReadMore>
                  <Link href={url}>
                    <Image src='/assets/icons/icon-explore-more.png' alt='read-more' width={136} height={18} />
                  </Link>
                </ReadMore>
              </>
            )}
          </>
        )}
      </Container>
      <BackgroundImage>
        <Image src='/assets/images/ai-bg.png' alt='ai-bg' width={375} height={259} />
      </BackgroundImage>
    </ContentWrapper>
  );
};

export default AsideAiNewsContent;
